import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BellOff, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { severityVariant, formatRelativeTime, statusColors } from "@/lib/formatters";
import { EnrichmentIndicator } from "@/components/alerts/enrichment-indicator";
import { presentBruteForceAlert } from "@/lib/brute-force-alert";
import type { Alert } from "@/types/api";

interface AlertTableProps {
  alerts: Alert[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onRowClick: (id: string) => void;
  isLoading: boolean;
  assigneeNames?: Record<string, string>;
  variant?: "table" | "queue";
  activeId?: string | null;
}

export function AlertTable({
  alerts,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onRowClick,
  isLoading,
  assigneeNames = {},
  variant = "table",
  activeId,
}: AlertTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        No alerts match your filters
      </div>
    );
  }

  const allSelected = alerts.length > 0 && alerts.every((a) => selectedIds.has(a.id));
  const someSelected = alerts.some((a) => selectedIds.has(a.id)) && !allSelected;

  if (variant === "queue") {
    return (
      <div className="space-y-2" aria-label="Alert queue results">
        <label className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            aria-label="Select all alerts on this page"
            checked={allSelected}
            ref={(element) => {
              if (element) element.indeterminate = someSelected;
            }}
            onChange={onSelectAll}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          Select page
        </label>
        <div className="space-y-2 p-2">
        {alerts.map((alert) => {
          const auth = presentBruteForceAlert(alert);
          const owner = alert.assigned_to
            ? assigneeNames[alert.assigned_to] ?? "Assigned analyst"
            : "Unassigned";
          return (
            <article
              key={alert.id}
              className={cn(
                "group flex items-start gap-2 rounded-lg border bg-card p-3 transition-colors hover:bg-secondary/70",
                activeId === alert.id ? "border-primary/40 bg-primary/10" : "border-border",
              )}
            >
              <input
                type="checkbox"
                aria-label={`Select ${alert.title}`}
                checked={selectedIds.has(alert.id)}
                onChange={() => onToggleSelect(alert.id)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary"
              />
              <button
                type="button"
                onClick={() => onRowClick(alert.id)}
                aria-current={activeId === alert.id ? "true" : undefined}
                className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex items-center gap-2">
                  <Badge variant={severityVariant(alert.severity)}>{alert.severity}</Badge>
                  <EnrichmentIndicator status={alert.enrichment_status} />
                  <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </span>
                <span className={cn(
                  "mt-2 block line-clamp-2 text-sm font-semibold leading-snug",
                  auth.successStatus === "success" && "text-destructive",
                )}>
                  {auth.isBruteForce && auth.successStatus === "success"
                    ? `Successful authentication · ${auth.successfulAccount ?? alert.title}`
                    : alert.title}
                </span>
                <span className="mt-2 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{alert.source_type || "Unknown source"}</span>
                  <span aria-hidden>·</span>
                  <span className="truncate">{owner}</span>
                  <span className="ml-auto shrink-0">{formatRelativeTime(alert.last_seen_at)}</span>
                </span>
              </button>
            </article>
          );
        })}
        </div>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[40px]">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={onSelectAll}
              className="h-4 w-4 rounded border-input accent-primary"
            />
          </TableHead>
          <TableHead className="w-[100px]">Severity</TableHead>
          <TableHead>Title</TableHead>
          <TableHead className="hidden md:table-cell">Source</TableHead>
          <TableHead className="hidden sm:table-cell">AI</TableHead>
          <TableHead className="hidden sm:table-cell">Status</TableHead>
          <TableHead className="hidden lg:table-cell">Owner</TableHead>
          <TableHead className="text-right">Last Seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {alerts.map((alert) => {
          const auth = presentBruteForceAlert(alert);
          const authVerdict =
            auth.successStatus === "success"
              ? `SUCCESSFUL AUTH${auth.successfulAccount ? ` · ${auth.successfulAccount}` : ""}${
                  auth.successfulIp ? ` · ${auth.successfulIp}` : ""
                }`
              : auth.successStatus === "none"
                ? "NO SUCCESSFUL AUTH"
                : "SUCCESS STATUS UNKNOWN";
          return (
          <TableRow
            key={alert.id}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(alert.id);
              }
            }}
            className={cn(
              "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              selectedIds.has(alert.id) && "bg-muted/50",
            )}
            data-state={selectedIds.has(alert.id) ? "selected" : undefined}
          >
            <TableCell>
              <input
                type="checkbox"
                checked={selectedIds.has(alert.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect(alert.id);
                }}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-input accent-primary"
              />
            </TableCell>
            <TableCell onClick={() => onRowClick(alert.id)}>
              <Badge variant={severityVariant(alert.severity)}>
                {alert.severity}
              </Badge>
            </TableCell>
            <TableCell
              onClick={() => onRowClick(alert.id)}
              className="max-w-[300px] font-medium"
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0">
                  <span className={cn("block truncate", auth.successStatus === "success" && "text-destructive")}>
                    {auth.isBruteForce ? authVerdict : alert.title}
                  </span>
                  {auth.isBruteForce && (
                    <span className="block truncate text-xs font-normal text-muted-foreground">
                      {auth.sourceCount > 0 && `${auth.sourceCount} sources · `}
                      {auth.failureCount > 0 && `${auth.failureCount.toLocaleString()} failures · `}
                      {alert.title}
                    </span>
                  )}
                </div>
                {alert.notification_suppressed && (
                  <Badge
                    variant="outline"
                    className="shrink-0 gap-1 text-muted-foreground"
                    title="A notification for this alert was suppressed by an active mute"
                  >
                    <BellOff className="h-3 w-3" />
                    Muted
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell
              onClick={() => onRowClick(alert.id)}
              className="hidden text-muted-foreground md:table-cell"
            >
              {alert.source_type || "---"}
            </TableCell>
            <TableCell
              onClick={() => onRowClick(alert.id)}
              className="hidden sm:table-cell"
            >
              <EnrichmentIndicator status={alert.enrichment_status} />
            </TableCell>
            <TableCell
              onClick={() => onRowClick(alert.id)}
              className="hidden sm:table-cell"
            >
              <span
                className={cn(
                  "text-xs font-medium capitalize",
                  statusColors[alert.status] ?? "text-muted-foreground",
                )}
              >
                {auth.isBruteForce && auth.firingCount > 1 ? "recurring" : alert.status}
              </span>
            </TableCell>
            <TableCell
              onClick={() => onRowClick(alert.id)}
              className="hidden max-w-[160px] truncate text-sm lg:table-cell"
              title={alert.assigned_to ?? "Unassigned"}
            >
              {alert.assigned_to ? (
                assigneeNames[alert.assigned_to] ?? "Assigned analyst"
              ) : (
                <span className="text-muted-foreground">Unassigned</span>
              )}
            </TableCell>
            <TableCell
              onClick={() => onRowClick(alert.id)}
              className="text-right text-sm text-muted-foreground"
            >
              {formatRelativeTime(alert.last_seen_at)}
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
